import React, { useEffect, useMemo, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Sprint, SprintStatus, Task, TaskPriority, Project } from '../../../types';
import { getProjectSprints, createSprint, updateSprint, moveTaskToSprint } from '../../../services/sprintService';
import { getProjectTasks } from '../../../services/taskService';
import ModuleGuard from '../../../components/ModuleGuard';
import Badge from '../../../components/Badge';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BM_LABEL, BM_INPUT, BM_TEXTAREA, BM_TITLE_INPUT,
} from '../../../components/BoardModal';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import RetrospectiveDrawer from './RetrospectiveDrawer';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

const STAT_LABEL: Record<number, string> = {
  100000000: 'Planned', 100000001: 'Active', 100000002: 'Completed', 100000003: 'Archived',
};
const STAT_BADGE: Record<number, { bg: string; fg: string }> = {
  100000000: { bg: '#F3F4F6', fg: '#5A6272' },
  100000001: { bg: '#D4EEF5', fg: '#0A5568' },
  100000002: { bg: '#D1FAE5', fg: '#065F46' },
  100000003: { bg: '#F3F4F6', fg: '#B8BEC9' },
};

const PRIORITY_BORDER: Record<number, string> = {
  [TaskPriority.Critical]: '#DC2626',
  [TaskPriority.High]:     '#D97706',
  [TaskPriority.Medium]:   '#0E7490',
  [TaskPriority.Low]:      '#9198A8',
};

export default function SprintsTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_agile">
      <SprintsTabBody {...props} />
    </ModuleGuard>
  );
}

function SprintsTabBody({ pca, projectId }: Props) {
  const [sprints, setSprints]   = useState<Sprint[]>([]);
  const [tasks, setTasks]       = useState<Task[]>([]);
  const [selected, setSelected] = useState<Sprint | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showCloseSprint, setShowCloseSprint] = useState<Sprint | null>(null);
  const [showRetro, setShowRetro] = useState<Sprint | null>(null);
  const [activeWarning, setActiveWarning] = useState(false);
  const me = useCurrentUser(pca, projectId);

  const reload = async () => {
    const [s, t] = await Promise.all([
      getProjectSprints(pca, projectId),
      getProjectTasks(pca, projectId).catch(() => [] as Task[]),
    ]);
    setSprints(s); setTasks(t);
    setSelected(prev => prev ? (s.find(x => x.dagrun_pm_sprintid === prev.dagrun_pm_sprintid) ?? null) : null);
  };
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  const setActive = async (sprint: Sprint) => {
    const anotherActive = sprints.some(s => s.dagrun_pm_status === SprintStatus.Active && s.dagrun_pm_sprintid !== sprint.dagrun_pm_sprintid);
    if (anotherActive) { setActiveWarning(true); return; }
    await updateSprint(pca, sprint.dagrun_pm_sprintid, { dagrun_pm_status: SprintStatus.Active });
    reload();
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 200px)', fontFamily: 'var(--font-ui)' }}>
      <div style={{ width: 280, flexShrink: 0, borderRight: '1px solid #DDE0E6', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#0F1117' }}>Sprints</h3>
          <button type="button" onClick={() => setShowCreate(true)}
            style={{ background: 'transparent', border: '1px solid #0E7490', color: '#0E7490', padding: '5px 10px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#D4EEF5')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >+ Create</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 16px 8px' }}>
          {sprints.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: '#9198A8', fontSize: 13 }}>No sprints yet.</div>
          )}
          {sprints.map(s => {
            const isSelected = selected?.dagrun_pm_sprintid === s.dagrun_pm_sprintid;
            const isActive = s.dagrun_pm_status === SprintStatus.Active;
            return (
              <div key={s.dagrun_pm_sprintid}
                onClick={() => setSelected(s)}
                style={{
                  background: isSelected ? '#F5F6F8' : '#fff',
                  borderLeft: isSelected || isActive ? '3px solid #0E7490' : '3px solid transparent',
                  border: '1px solid #EAECF0',
                  borderRadius: 6, padding: '10px 12px',
                  marginBottom: 6, cursor: 'pointer',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => !isSelected && (e.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={e => !isSelected && (e.currentTarget.style.background = '#fff')}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0F1117', marginBottom: 4 }}>{s.dagrun_pm_name}</div>
                {s.dagrun_pm_sprintgoal && (
                  <div style={{
                    fontSize: 12, color: '#5A6272', fontStyle: 'italic',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden', marginBottom: 6,
                  }}>{s.dagrun_pm_sprintgoal}</div>
                )}
                <div style={{ fontSize: 10, color: '#9198A8', fontFamily: 'var(--font-mono)', marginBottom: 6 }}>
                  {s.dagrun_pm_startdate ? new Date(s.dagrun_pm_startdate).toLocaleDateString() : '—'}
                  {' → '}
                  {s.dagrun_pm_enddate ? new Date(s.dagrun_pm_enddate).toLocaleDateString() : '—'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                  <Badge {...STAT_BADGE[s.dagrun_pm_status]}>{STAT_LABEL[s.dagrun_pm_status]}</Badge>
                  {me?.isPM && s.dagrun_pm_status === SprintStatus.Planned && (
                    <button type="button" onClick={e => { e.stopPropagation(); setActive(s); }}
                      style={{ background: 'transparent', border: 'none', color: '#0E7490', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                    >Set Active</button>
                  )}
                  {me?.isPM && s.dagrun_pm_status === SprintStatus.Active && (
                    <button type="button" onClick={e => { e.stopPropagation(); setShowCloseSprint(s); }}
                      style={{ background: 'transparent', border: '1px solid #DC2626', color: '#DC2626', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}
                    >Close Sprint</button>
                  )}
                  {s.dagrun_pm_status === SprintStatus.Completed && (
                    <button type="button" onClick={e => { e.stopPropagation(); setShowRetro(s); }}
                      style={{ background: 'transparent', border: 'none', color: '#0E7490', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                    >Retrospective</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {selected
          ? <SprintBoard pca={pca} sprint={selected} tasks={tasks} onChange={reload} />
          : <div style={{ padding: 80, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>Select a sprint from the list to view its board.</div>}
      </div>

      {showCreate && (
        <CreateSprintModal pca={pca} projectId={projectId} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); }} />
      )}
      {showCloseSprint && (
        <CloseSprintModal pca={pca} sprint={showCloseSprint} sprints={sprints} tasks={tasks}
          onClose={() => setShowCloseSprint(null)}
          onClosed={() => { setShowCloseSprint(null); reload(); }}
        />
      )}
      {activeWarning && (
        <BoardModal open title="Active sprint already exists" onClose={() => setActiveWarning(false)}
          footer={<BMPrimaryButton onClick={() => setActiveWarning(false)}>Close</BMPrimaryButton>}>
          A sprint is already active. You must close it before activating another.
        </BoardModal>
      )}
      {showRetro && (
        <RetrospectiveDrawer pca={pca} sprint={showRetro} projectId={projectId} onClose={() => setShowRetro(null)} />
      )}
    </div>
  );
}

function SprintBoard({ pca, sprint, tasks, onChange }: { pca: IPublicClientApplication; sprint: Sprint; tasks: Task[]; onChange: () => void }) {
  const backlog = useMemo(() => tasks.filter(t => !t._dagrun_pm_sprintid_value), [tasks]);
  const inSprint = useMemo(() => tasks.filter(t => t._dagrun_pm_sprintid_value === sprint.dagrun_pm_sprintid), [tasks, sprint]);
  const totalPoints = inSprint.reduce((s, t) => s + (t.dagrun_pm_storypoints ?? 0), 0);

  const move = async (taskId: string, sprintId: string | null) => {
    await moveTaskToSprint(pca, taskId, sprintId);
    onChange();
  };

  return (
    <div>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #EAECF0' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 18, fontWeight: 600, color: '#0F1117' }}>{sprint.dagrun_pm_name}</span>
          <span style={{ fontSize: 12, color: '#9198A8', fontFamily: 'var(--font-mono)' }}>
            {sprint.dagrun_pm_startdate ? new Date(sprint.dagrun_pm_startdate).toLocaleDateString() : '—'}
            {' → '}
            {sprint.dagrun_pm_enddate ? new Date(sprint.dagrun_pm_enddate).toLocaleDateString() : '—'}
          </span>
          <Badge {...STAT_BADGE[sprint.dagrun_pm_status]}>{STAT_LABEL[sprint.dagrun_pm_status]}</Badge>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: 16 }}>
        <BoardColumn title="Backlog" count={backlog.length}>
          {backlog.length === 0 && <Empty>No backlog tasks.</Empty>}
          {backlog.map(t => (
            <SprintTaskRow key={t.dagrun_pm_taskid} task={t}
              actionLabel="→ Add to Sprint"
              onAction={() => move(t.dagrun_pm_taskid, sprint.dagrun_pm_sprintid)}
            />
          ))}
        </BoardColumn>

        <BoardColumn title="Sprint Backlog" count={inSprint.length} pointsTotal={totalPoints}>
          {inSprint.length === 0 && <Empty>No tasks in this sprint yet.</Empty>}
          {inSprint.map(t => (
            <SprintTaskRow key={t.dagrun_pm_taskid} task={t}
              actionLabel="← Remove"
              onAction={() => move(t.dagrun_pm_taskid, null)}
              actionMuted
            />
          ))}
        </BoardColumn>
      </div>
    </div>
  );
}

function BoardColumn({ title, count, pointsTotal, children }: { title: string; count: number; pointsTotal?: number; children: React.ReactNode }) {
  return (
    <div style={{ background: '#F5F6F8', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#9198A8', fontFamily: 'var(--font-mono)' }}>{count} task{count === 1 ? '' : 's'}</span>
          {pointsTotal != null && pointsTotal > 0 && (
            <span style={{ background: '#D4EEF5', color: '#0A5568', fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 10, fontFamily: 'var(--font-mono)' }}>{pointsTotal} pts</span>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function SprintTaskRow({ task, actionLabel, onAction, actionMuted }: { task: Task; actionLabel: string; onAction: () => void; actionMuted?: boolean }) {
  const left = PRIORITY_BORDER[task.dagrun_pm_priority] ?? '#DDE0E6';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: '#fff',
      borderTop: '1px solid #EAECF0', borderRight: '1px solid #EAECF0', borderBottom: '1px solid #EAECF0',
      borderLeft: `3px solid ${left}`,
      borderRadius: 6, padding: '8px 10px', marginBottom: 6,
    }}>
      <span style={{ flex: 1, fontSize: 13, color: '#0F1117', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.dagrun_pm_name}</span>
      {task.dagrun_pm_assigneename && (
        <div title={task.dagrun_pm_assigneename} style={{ width: 20, height: 20, borderRadius: '50%', background: '#D4EEF5', color: '#0A5568', fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {task.dagrun_pm_assigneename.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase()}
        </div>
      )}
      {task.dagrun_pm_storypoints != null && (
        <span style={{ background: '#D4EEF5', color: '#0A5568', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: '50%', minWidth: 20, textAlign: 'center', fontFamily: 'var(--font-mono)' }}>
          {task.dagrun_pm_storypoints}
        </span>
      )}
      <button type="button" onClick={onAction}
        style={{
          background: 'transparent',
          border: `1px solid ${actionMuted ? '#DDE0E6' : '#0E7490'}`,
          color: actionMuted ? '#5A6272' : '#0E7490',
          padding: '3px 8px', borderRadius: 4,
          fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600,
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >{actionLabel}</button>
    </div>
  );
}

const Empty = ({ children }: { children: React.ReactNode }) => (
  <div style={{ padding: 16, textAlign: 'center', color: '#9198A8', fontSize: 12 }}>{children}</div>
);

function CreateSprintModal({ pca, projectId, onClose, onCreated }: { pca: IPublicClientApplication; projectId: string; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || !start || !end || saving) return;
    setSaving(true); setError(null);
    try {
      await createSprint(pca, projectId, name.trim(), goal.trim(), start, end);
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Create Sprint" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim() || !start || !end}>{saving ? 'Creating…' : 'Create Sprint'}</BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Sprint Name *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} style={BM_TITLE_INPUT} placeholder="e.g. Sprint 14" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Sprint Goal</label>
        <textarea value={goal} onChange={e => setGoal(e.target.value)} style={BM_TEXTAREA} placeholder="What is this sprint trying to achieve?" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={BM_LABEL}>Start Date *</label>
          <input type="date" value={start} onChange={e => setStart(e.target.value)} style={BM_INPUT} />
        </div>
        <div>
          <label style={BM_LABEL}>End Date *</label>
          <input type="date" value={end} onChange={e => setEnd(e.target.value)} style={BM_INPUT} />
        </div>
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

function CloseSprintModal({ pca, sprint, sprints, tasks, onClose, onClosed }: { pca: IPublicClientApplication; sprint: Sprint; sprints: Sprint[]; tasks: Task[]; onClose: () => void; onClosed: () => void }) {
  const incomplete = useMemo(() =>
    tasks.filter(t => t._dagrun_pm_sprintid_value === sprint.dagrun_pm_sprintid && t.dagrun_pm_status !== 100000003),
    [tasks, sprint]);
  const targets = sprints.filter(s => s.dagrun_pm_status === SprintStatus.Planned && s.dagrun_pm_sprintid !== sprint.dagrun_pm_sprintid);
  const [destination, setDestination] = useState<string>('backlog');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setWorking(true); setError(null);
    try {
      const targetSprintId = destination === 'backlog' ? null : destination;
      for (const t of incomplete) {
        await moveTaskToSprint(pca, t.dagrun_pm_taskid, targetSprintId);
      }
      await updateSprint(pca, sprint.dagrun_pm_sprintid, { dagrun_pm_status: SprintStatus.Completed });
      onClosed();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Close failed');
    } finally { setWorking(false); }
  };

  return (
    <BoardModal open title="Close Sprint" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={working} />
        <BMPrimaryButton onClick={submit} disabled={working}>{working ? 'Closing…' : 'Close Sprint'}</BMPrimaryButton>
      </>}>
      {incomplete.length === 0 ? (
        <div>All tasks in this sprint are Done. Closing the sprint will mark it Completed.</div>
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <strong>{incomplete.length}</strong> task{incomplete.length === 1 ? ' is' : 's are'} incomplete in this sprint. Where should they go?
          </div>
          <label style={BM_LABEL}>Move incomplete tasks to</label>
          <select value={destination} onChange={e => setDestination(e.target.value)} style={BM_INPUT}>
            <option value="backlog">Project Backlog</option>
            {targets.map(s => <option key={s.dagrun_pm_sprintid} value={s.dagrun_pm_sprintid}>{s.dagrun_pm_name}</option>)}
          </select>
        </>
      )}
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}
